/**
 * @file useDealsRegistry.ts
 * @description Данные реестра сделок (phase19.31, макет (в)): страница
 * сделок по фильтрам из адреса, воронки для фильтра и окна «Новая сделка»,
 * счётчики для подписи «16 сделок · 11 открытых».
 *
 * Счётчики — по всем сделкам магазина, без фильтров страницы (подпись
 * говорит о магазине, а не о текущем отборе): два лёгких запроса `limit=1`,
 * берётся только `total`. «Открытые» считает сервер тем же признаком, что и
 * фильтр «Только открытые».
 */

import { useCallback, useEffect, useState } from "react";

import { useUrlRegistry, type UrlRegistrySpec, type UrlRegistryState } from "../useUrlRegistry";
import { listDeals, listPipelines, type DealDto, type PipelineDto } from "../../../lib/api-crm";
import {
  parseDealsQuery,
  resetDealsQuery,
  toListDealsParams,
  writeDealsQuery,
  type DealsRegistryQuery,
} from "../../../lib/crm-deals-query";

/** Тексты ошибок: без списка и со списком на экране. */
export const DEALS_ERROR_TEXT = {
  empty: "Не удалось загрузить сделки. Проверьте связь и попробуйте ещё раз",
  stale: "Не удалось обновить список, данные могут быть устаревшими",
} as const;

const loadDeals = (query: DealsRegistryQuery) => listDeals(toListDealsParams(query));

const SPEC: UrlRegistrySpec<DealsRegistryQuery, DealDto> = {
  parse: parseDealsQuery,
  write: writeDealsQuery,
  load: loadDeals,
  reset: resetDealsQuery,
  errorText: DEALS_ERROR_TEXT,
};

export interface DealsRegistry extends UrlRegistryState<DealsRegistryQuery, DealDto> {
  pipelines: PipelineDto[];
  /** Всего сделок и открытых; `null` — ещё не загружено. */
  counts: { total: number; open: number } | null;
}

export function useDealsRegistry(): DealsRegistry {
  const registry = useUrlRegistry(SPEC);
  const [pipelines, setPipelines] = useState<PipelineDto[]>([]);
  const [counts, setCounts] = useState<{ total: number; open: number } | null>(null);

  const loadCounts = useCallback(() => {
    Promise.all([listDeals({ limit: 1 }), listDeals({ openOnly: true, limit: 1 })])
      .then(([all, open]) => setCounts({ total: all.total, open: open.total }))
      // Подпись — не главное на странице: без счётчиков список работает.
      .catch(() => setCounts(null));
  }, []);

  useEffect(() => {
    listPipelines()
      .then((res) => setPipelines(res.items))
      .catch(() => setPipelines([]));
    loadCounts();
  }, [loadCounts]);

  const { refresh: refreshList } = registry;
  const refresh = useCallback(() => {
    refreshList();
    loadCounts();
  }, [refreshList, loadCounts]);

  return { ...registry, refresh, pipelines, counts };
}
